/*
 * Copyright (C) 2026 - present Marek Kuzora - All Rights Reserved.
 */


import { argKey } from 'solis/navigation';

import Args from 'solis/claude/action/args';

import BoardFrontierType from 'solis/logic/board/frontier.type';
import BoardProximity from 'solis/logic/board/proximity';
import ZoneUsers from 'solis/logic/board/zone/users';
import ZoneInfluences from 'solis/logic/board/zone/influences';
import InfluenceCass from 'solis/logic/board/influence/cass';


// cpp create_board_influence_args minus `user`. The source colony is the operating
// perspective; its `currentInfluence` bounds how much can be placed.
const buildArgs = ({ board, sourceColony, zone, args }) =>
{
  const payload = {};

  payload.galaxy = board.cass.galaxy;
  payload.board = board.cass.id;
  payload.colony = sourceColony.cass.id;
  payload.target_zone = zone.getAbsoluteHex().toString();

  payload.value = args.value;

  return payload;
};


// Mirrors the cpp create_board_influence checks, in handler order. Source user existence
// and colony ownership are handled upstream: useSourceColony only offers colonies whose
// origin user is the account.
const validate = ({ board, sourceColony, zone, account, now, args }) =>
{
  const settings = board.getSettings();

  const zoneUser = new ZoneUsers(zone);

  const existing = zone.getInfluence(sourceColony.cass.id);

  const delta = args.value - (existing?.cass.value ?? 0);

  const issues = [];

  if (!zoneUser.isNeighborAccessible(new BoardProximity(board, BoardFrontierType.Zone), sourceColony.cass.id))
  {
    issues.push('Zone is not reachable from the source colony');
  }

  if (!zoneUser.isEstablished(account.cass.id))
  {
    issues.push('You are not established in this zone');
  }

  if (delta < 0)
  {
    issues.push('Existing influence cannot be lowered');
  }

  if (sourceColony.cass.currentInfluence < delta)
  {
    issues.push('Not enough influence: need ' + delta + ', have ' + sourceColony.cass.currentInfluence);
  }

  if (new ZoneInfluences(zone).getUserInfluenceValue(account.cass.id) + delta > settings.getInfluenceMaxValue())
  {
    issues.push('Total influence in the zone cannot exceed ' + settings.getInfluenceMaxValue());
  }

  if (existing !== null && !new InfluenceCass(existing).canReset(now))
  {
    issues.push('Too early to reset this influence');
  }

  return { valid: issues.length === 0 && args.value > 0, issues };
};


const isAvailable = ({ sourceColony, zone }) =>
{
  return sourceColony !== null && zone !== null;
};


export default
{
  type: 'solis/board/influence/create',
  scope: argKey(Args.BoardZone, 'createInfluence'),
  inputs: { value: 0 },

  isAvailable,
  buildArgs,
  validate,
};
